import React, { ReactNode, useState } from 'react';
import { FormInstance, Space } from 'antd';
import { useMutation } from '@common/hooks';
import { createMessage } from 'works/legacy/lib/message-helper';
import {
  Banner,
  Button,
  ButtonGroup,
  Modal,
  PrimaryButton,
} from '@common/components';
import {
  SuppliersManagementTierForm,
  SuppliersManagementTierFormValues,
} from './_suppliers-management-tier-form';
import { UpdateTierMutation } from '@institutions/graphql';

type SuppliersManagementUpdateTierModalProps = {
  tier: SuppliersManagementTierFormValues & { id: string };
  visible: boolean;
  setVisible: (visible: boolean) => void;
  onUpdate: (tierId: string) => void;
  actions?: ReactNode;
};

export function SuppliersManagementUpdateTierModal({
  tier,
  visible,
  setVisible,
  onUpdate,
  actions,
}: SuppliersManagementUpdateTierModalProps) {
  const [formInstance, setFormInstance] =
    useState<FormInstance<SuppliersManagementTierFormValues>>();

  const [mutation, { loading }] = useMutation(UpdateTierMutation, {
    onCompleted: (data) => {
      if (data.updateTier?.tier) {
        onUpdate(data.updateTier.tier.id);
        createMessage(
          'success',
          `${data.updateTier.tier.name} successfully saved`
        );
        setVisible(false);
      } else if (data.updateTier?.errors) {
        createMessage('error', data.updateTier.errors[0].message);
      }
    },
    onError: () => {
      createMessage('error', 'Failed to perform action. Please try again.');
    },
    refetchQueries: ['Suppliers', 'StaffingRequestTiersVisibilityQuery'],
  });

  const onOk = () => {
    formInstance?.submit();
  };

  const onCancel = () => {
    setVisible(false);
  };

  const onFinish = (values: SuppliersManagementTierFormValues) => {
    mutation({ variables: { tierId: tier.id, input: values } });
  };

  const Footer = (
    <ButtonGroup align="right">
      <Button key="cancel" size="md" onClick={onCancel} loading={loading}>
        Cancel
      </Button>
      <PrimaryButton key="submit" onClick={onOk} loading={loading}>
        Save
      </PrimaryButton>
    </ButtonGroup>
  );

  return (
    <>
      <Modal
        onOk={onOk}
        onCancel={onCancel}
        visible={visible}
        title="Edit Tier"
        footer={Footer}
        destroyOnClose
        className="suppliers-management-update-tier-modal"
      >
        <Space direction="vertical" size="middle">
          <Banner type="info">
            Saved changes may affect the Staffing Request visibility for
            Suppliers in this Tier. Active Submissions and Billings will remain
            visible.
          </Banner>
          <SuppliersManagementTierForm
            onFinish={onFinish}
            onFormInstanceReady={(instance) => {
              setFormInstance(instance);
            }}
            initialValues={tier}
            actions={actions}
          />
        </Space>
      </Modal>
      <style jsx>{`
        :global(.suppliers-management-update-tier-modal .ant-modal-body) {
          padding-top: 16px;
        }
      `}</style>
    </>
  );
}
